/* eslint-disable max-lines */
import groupBy from 'lodash/groupBy';
import sumBy from 'lodash/sumBy';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  buildUseLazyQuery,
  buildUseMutation,
  convertPenumbraJsonFilesToCsv,
  PENUMBRA_IGNORE_AUTH_TAG_THRESHOLD,
} from '@main/core-ui';
import { DownloadFileOrderField } from '@main/dsr-types';
import type {
  DownloadStatusesLookup,
  PenumbraFile,
  PenumbraZipWriter,
  RemoteResource,
} from '@main/pc-core-components';
import { penumbra } from '@main/pc-core-components';
import { endpoints } from '@main/pc-types';
import type { ID } from '@main/schema-utils';
import { OrderDirection } from '@main/schema-utils';

import { useDownloadZipMessages } from './messages';
import type { DownloadZipFn } from './types';
import { useCheckSombraScopesError } from './useCheckSombraScopesError';
import { useDiffieHellman } from './useDiffieHellman';

// we are limited by s3 to 6 streams
const PAGE_SIZE = 5;

/**
 * Download file
 */
type DownloadFile = RemoteResource & {
  /** File size */
  size: number;
  /** File name */
  fileName: string;
};

/** Mutation to download CEK context */
const useDataSubjectDownloadCEK = buildUseMutation(
  endpoints.dataSubjectDownloadCEK,
  'DownloadAccessZip',
);

/** Fetch a page of files */
const useDownloadFiles = buildUseLazyQuery(
  endpoints.downloadFiles,
  'PcDownloadZipFiles',
  {
    nodes: {
      id: null,
      fileName: null,
      title: {
        id: null,
        defaultMessage: null,
        description: null,
      },
      size: null,
      downloadUrl: null,
      mimetype: null,
      authTag: null,
      iv: null,
    },
    totalSize: null,
  },
);

const useDownloadFilesCountsOnly = buildUseLazyQuery(
  endpoints.downloadFilesCountsOnly,
  'PcDownloadZipFilesCountsOnly',
  {
    nodes: {
      dataPointId: null,
      name: null,
      title: {
        id: null,
        defaultMessage: null,
        description: null,
      },
      fileCount: null,
    },
  },
);

/**
 * Gets remote files, downloads and - if necessary - decrypts them, then saves them as a .zip
 *
 * @returns A function that will download the zip and the progress indicator
 */
export function useDownloadZip(): [DownloadZipFn, DownloadStatusesLookup] {
  const [createRequestFileDhEncrypted] = useDiffieHellman();
  const [createDhEncrypted, decryptResponse] = useDiffieHellman();
  const checkSombraScopesError = useCheckSombraScopesError();

  /** Lazy query to page over files in zip */
  const getDownloadFiles = useDownloadFiles();
  const getDownloadFilesCountsOnly = useDownloadFilesCountsOnly();

  /** Mutation to get the CEK for decrypting files */
  const [dataSubjectDownloadCEK, { error: dataSubjectDownloadCEKError }] =
    useDataSubjectDownloadCEK();

  /** For internationalization */
  const { formatMessage } = useIntl();

  /** Save progress of zip */
  const [downloadStatuses, setDownloadStatuses] =
    useState<DownloadStatusesLookup>({});

  useEffect(() => {
    if (dataSubjectDownloadCEKError) {
      checkSombraScopesError(dataSubjectDownloadCEKError.message);
    }
  }, [dataSubjectDownloadCEKError]);

  const getFilePage: (
    requestId: ID<'request'>,
    offset: number,
    unwrappedCEK: Buffer,
  ) => Promise<[DownloadFile[], number]> = useCallback(
    async (requestId, offset, unwrappedCEK) => {
      // No dh channel established
      if (!createRequestFileDhEncrypted) {
        throw new Error('Could not authenticate');
      }

      const { data, error } = await getDownloadFiles({
        first: PAGE_SIZE,
        offset,
        dhEncrypted: createRequestFileDhEncrypted(),
        filterBy: { requestId },
        orderBy: [
          // we sort by size to more efficiently use our chunked connections
          {
            field: DownloadFileOrderField.Size,
            direction: OrderDirection.DESC,
          },
          {
            field: DownloadFileOrderField.CreatedAt,
            direction: OrderDirection.DESC,
          },
        ],
      });
      if (error) {
        checkSombraScopesError(error.message);
      }
      if (!data) {
        throw new Error(`Fetching page "${offset}" failed`);
      }

      return [
        data.nodes.map((downloadFile) => ({
          path: downloadFile.fileName,
          url: downloadFile.downloadUrl,
          mimetype: downloadFile.mimetype,
          size: +downloadFile.size,
          decryptionOptions: {
            authTag: downloadFile.authTag,
            iv: downloadFile.iv,
            key: unwrappedCEK,
          },
          fileName: downloadFile.fileName,
        })),
        +data.totalSize,
      ];
    },
    [createRequestFileDhEncrypted],
  );

  const getFilePageCountsOnly: (requestId: ID<'request'>) => Promise<
    {
      /** ID of dataPoint */
      dataPointId: string;
      /** Name of dataPoint */
      name: string;
      /** Translated title of dataPoint */
      title?: string;
      /** The number of files found for datapoint */
      fileCount: number;
    }[]
  > = useCallback(
    async (requestId) => {
      // No dh channel established
      if (!createRequestFileDhEncrypted) {
        throw new Error('Could not authenticate');
      }

      const { data, error } = await getDownloadFilesCountsOnly({
        dhEncrypted: createRequestFileDhEncrypted(),
        filterBy: { requestId },
      });

      if (error) {
        checkSombraScopesError(error.message);
      }

      return data.nodes.map((downloadFile) => ({
        dataPointId: downloadFile.dataPointId,
        name: downloadFile.name,
        title: downloadFile.title
          ? formatMessage(downloadFile.title)
          : undefined,
        fileCount: downloadFile.fileCount,
      }));
    },
    [createRequestFileDhEncrypted],
  );

  return [
    async (requestId, { report, zipFileName, convertJSONtoCSV }) => {
      setDownloadStatuses((lookup) => ({
        ...lookup,
        [requestId]: {
          isDownloading: true,
          progress: 0,
        },
      }));

      // No dh channel established
      if (!createDhEncrypted) {
        setDownloadStatuses((lookup) => ({
          ...lookup,
          [requestId]: {
            isDownloading: false,
            progress: 0,
          },
        }));
        throw new Error('Could not authenticate');
      }

      // Download the CEK to decrypt the files
      const { data } = await dataSubjectDownloadCEK({
        variables: { id: requestId, dhEncrypted: createDhEncrypted() },
      });
      if (!data?.decryptionContext) {
        setDownloadStatuses((lookup) => ({
          ...lookup,
          [requestId]: {
            isDownloading: false,
            progress: 0,
          },
        }));
        throw new Error(
          'An error occurred while authenticating for zip download',
        );
      }

      const unwrappedResponse = decryptResponse(data.decryptionContext);

      // Loop over all files until there are no more
      let pageCount = PAGE_SIZE;
      let totalFetched = 0;

      const pagesRaw: DownloadFile[] = [];
      let totalSize = 0;

      // Collate all files
      /* eslint-disable no-await-in-loop */
      while (pageCount === PAGE_SIZE) {
        const [files, fileSize] = await getFilePage(
          requestId,
          totalFetched,
          unwrappedResponse,
        );

        pagesRaw.push(...files);
        totalSize += fileSize;

        // Keep track of what was processed
        totalFetched += files.length;
        pageCount = +files.length;
      }

      // Page by file names to help with concatenating files to CSV
      const groupedFiles = groupBy(pagesRaw, 'fileName');
      const pages = Object.entries(groupedFiles).map(
        ([fileName, fileGroup]) => ({
          fileName,
          totalSize: sumBy(fileGroup, 'size'),
          fileParts: fileGroup,
        }),
      );

      // Initialize zip writer
      const writer: PenumbraZipWriter = penumbra.saveZip({
        name: zipFileName,
        size: totalSize,
        /**
         * Progress handler
         *
         * @param event - Zip progress emit
         */
        onProgress({ detail: { percent } }) {
          if (typeof percent === 'number' && !Number.isNaN(percent)) {
            setDownloadStatuses((lookup) => ({
              ...lookup,
              [requestId]: {
                isDownloading: true,
                progress: percent,
              },
            }));
          }
        },
      });

      /**
       * Iterate through each of the page, which is already divided by file name
       * and then write them to the zip
       */
      // eslint-disable-next-line no-restricted-syntax
      for await (const { totalSize, fileParts } of pages) {
        if (typeof writer.byteSize === 'number') {
          writer.byteSize += totalSize;
        } else {
          writer.byteSize = totalSize ?? null;
        }

        // For each file, add it to this folder in the zip
        // TODO: remove ignoreAuthTag https://transcend.height.app/T-33779
        const fetchedFiles = await penumbra.get(
          ...fileParts.map((f) => ({
            ...f,
            ignoreAuthTag: f.size > PENUMBRA_IGNORE_AUTH_TAG_THRESHOLD,
          })),
        );

        const filesToWrite: PenumbraFile[] = [];
        if (!convertJSONtoCSV) {
          filesToWrite.push(...fetchedFiles);
        } else {
          filesToWrite.push(
            ...(await convertPenumbraJsonFilesToCsv(fetchedFiles, {
              groupFilesByPath: true,
            })),
          );
        }

        await writer.write(...filesToWrite);
      }
      /* eslint-enable no-await-in-loop */

      // Getting COUNTS_ONLY files
      const fileCounts = await getFilePageCountsOnly(requestId);
      fileCounts.forEach((file) => {
        writer.write({
          path: `${file.name}.txt`,
          mimetype: 'text/plain',
          stream: new Response(
            formatMessage(useDownloadZipMessages.countsOnlyMessage, {
              count: file.fileCount,
              title: file.title ?? file.name,
            }),
          ).body!,
        });
      });

      writer.write({
        path: 'report.html',
        mimetype: 'text/html',

        stream: new Response(report).body!,
      });
      setDownloadStatuses((lookup) => ({
        ...lookup,
        [requestId]: {
          isDownloading: false,
          progress: 100,
        },
      }));
      await writer.close();
    },
    downloadStatuses,
  ];
}
/* eslint-enable max-lines */
